import { useListDomains, useDeleteDomain, getListDomainsQueryKey } from "@workspace/api-client-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Link, useLocation } from "wouter";
import { Plus, Globe, FileText, Gauge, Activity, ArrowRight, Sparkles, Trash2, Loader2, Square, Check, Play, RotateCcw, AlertCircle, Crosshair } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";
import { useActiveDomain } from "@/hooks/use-active-domain";
import { SiteFavicon } from "@/components/site-favicon";
import { isActiveCrawlStatus } from "@/hooks/use-crawl-completion-toast";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

const statusLabels: Record<string, string> = {
  pending: "Bekliyor", crawling: "Taranıyor", linking: "Linkler işleniyor", clustering: "Cluster oluşturuluyor",
  analyzing: "Analiz", done: "Tamamlandı", error: "Hata",
  awaiting_approval: "Onay bekliyor", stopped: "Durduruldu",
};
const statusColor: Record<string, string> = {
  pending: "#70706c", crawling: "#ec6461", linking: "#ec6461", clustering: "#e83e3c",
  analyzing: "#e83e3c", done: "#12b76a", error: "#f04438",
  awaiting_approval: "#f79009", stopped: "#fb6514",
};

// awaiting_approval domain için SADE onay kartı. Onay HER ZAMAN ücretsizdir (yalnız cluster'ı
// kabul edip skor+yapısal analize geçirir) — toplu AI link önerisi burada SORULMAZ; o, Link
// İnşaası sayfasında (bir domaine girince) ayrı, açıkça maliyetli, opt-in bir aksiyondur.
function ApprovalCard({ domain }: { domain: { id: number; url: string } }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [submitting, setSubmitting] = useState(false);

  const approve = async () => {
    setSubmitting(true);
    try {
      const r = await fetch(`/api/domains/${domain.id}/approve`, {
        method: "POST", headers: { "content-type": "application/json" },
        body: JSON.stringify({ skipLinks: true }),
      });
      if (!r.ok) throw new Error();
      toast({ title: "Onaylandı", description: "Link önerilerini Link İnşaası sayfasında sayfa-sayfa (veya toplu) üretebilirsin." });
      queryClient.invalidateQueries({ queryKey: getListDomainsQueryKey() });
    } catch {
      toast({ title: "Onay başarısız", variant: "destructive" });
    } finally { setSubmitting(false); }
  };

  return (
    <Card style={{ borderColor: "#12b76a80", background: "rgba(34,197,94,0.06)" }}>
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-1">
          <Sparkles className="w-4 h-4 text-success-500" />
          <span className="font-semibold truncate">{domain.url}</span>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Sizi detaylı inceledim ve cluster yapınızı çıkardım. Cluster + sayfalar + içerikler <strong>zaten kaydedildi</strong>.
          Onayla — link önerisini istediğin zaman Link İnşaası sayfasında (sayfa-sayfa ya da toplu) üretebilirsin.
        </p>
        <div className="flex items-center gap-3 flex-wrap">
          <Button onClick={approve} disabled={submitting} className="gap-2">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            Onayla
          </Button>
          <Link href={`/domains/${domain.id}/clusters`}>
            <Button variant="ghost" size="sm" className="gap-1">Cluster yapısını gör <ArrowRight className="w-4 h-4" /></Button>
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}

// SEÇİLİ DOMAİN — AJAN ÖNERİLERİ: dashboard, aktif site özelinde çalışır ve
// ajanlarımızın öncelikli teknik önerilerini (Aksiyon Planı) öne çıkarır.
function AgentRecommendations({ domainId, domainUrl }: { domainId: number; domainUrl: string }) {
  const [, setLocation] = useLocation();
  const { data } = useQuery({
    queryKey: ["action-plan", domainId],
    queryFn: async () => {
      const r = await fetch(`/api/domains/${domainId}/action-plan`);
      return r.ok ? r.json() as Promise<{ plan: { verdict: string; items: { id?: string; title: string; priority: string; why: string; done?: boolean; path: string }[] } | null }> : { plan: null };
    },
  });
  const items = (data?.plan?.items ?? []).filter((i) => !i.done).slice(0, 5);
  const priColor: Record<string, string> = { kritik: "#d92d20", yuksek: "#d97706", orta: "#2563eb" };

  return (
    <Card className="border-brand-200 bg-brand-25 dark:border-brand-500/30 dark:bg-brand-500/[0.06]">
      <CardContent className="p-5">
        <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
          <div className="flex items-center gap-2">
            <Crosshair className="w-4 h-4 text-warning-700 dark:text-warning-400" />
            <span className="font-semibold text-sm">Ajan Önerileri · <span className="text-warning-700 dark:text-warning-400">{domainUrl.replace(/^https?:\/\//, "").replace(/\/$/, "")}</span></span>
          </div>
          <Button size="sm" variant="ghost" className="gap-1 text-[12px]" onClick={() => setLocation("/planning")}>
            Tüm plan <ArrowRight className="w-3.5 h-3.5" />
          </Button>
        </div>
        {data?.plan?.verdict && <p className="text-[13px] text-muted-foreground mb-3">{data.plan.verdict}</p>}
        {items.length === 0 ? (
          <div className="text-[13px] text-muted-foreground">
            Bu site için henüz öneri üretilmedi.{" "}
            <button className="underline font-medium text-warning-700 dark:text-warning-400" onClick={() => setLocation("/planning")}>Aksiyon Planı oluştur</button> — tüm ajan verileri okunup öncelik sırası çıkar.
          </div>
        ) : (
          <div className="space-y-1.5">
            {items.map((it, i) => (
              <button key={it.id ?? i} onClick={() => setLocation(it.path)}
                className="border-gray-100 dark:border-gray-800 w-full text-left flex items-center gap-2.5 rounded-xl border bg-white px-3 py-2 hover:shadow-sm transition"
                >
                <span className="w-5 h-5 rounded-md flex items-center justify-center text-[11px] font-bold shrink-0" style={{ background: `${priColor[it.priority] ?? "#2563eb"}18`, color: priColor[it.priority] ?? "#2563eb" }}>{i + 1}</span>
                <span className="text-[13px] font-medium flex-1 truncate">{it.title}</span>
                <ArrowRight className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
              </button>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Tarama aşamasını adım adım anlatan kısa metin (Geolop Asistanı hissi)
function crawlStepText(d: { status: string; crawledPages?: number | null; totalPages?: number | null }) {
  const c = d.crawledPages ?? 0, t = d.totalPages ?? 0;
  switch (d.status) {
    case "pending": return "Sıraya alındı, birazdan başlıyoruz…";
    case "crawling": return t > 0 ? `Sayfalar taranıyor — ${c}/${t}` : "Site haritası çıkarılıyor, sayfalar keşfediliyor…";
    case "linking": return "İç link grafiği çıkarılıyor…";
    case "clustering": return "Konu kümeleri (cluster) oluşturuluyor…";
    case "analyzing": return "Link önerileri üretiliyor…";
    case "awaiting_approval": return "Cluster yapısı hazır — onayını bekliyorum.";
    case "stopped": return "Tarama durduruldu — \"Devam Et\" ile kaldığı yerden sürdürebilirsin.";
    case "error": return "İşlem hata ile sonlandı — \"Devam Et\" kaldığı aşamadan tekrar dener.";
    default: return "";
  }
}

export default function Dashboard() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const deleteDomain = useDeleteDomain();

  const { data: domains, isLoading } = useListDomains({
    query: {
      // queryKey acikca verilir: UseQueryOptions bu surumde zorunlu tutuyor,
      // uretilen hook kendi icinde dolduruyor ama tip cagirandan bekliyor.
      queryKey: getListDomainsQueryKey(),
      // aktif tarama varsa 2.5sn'de bir yenile (canlı ilerleme)
      refetchInterval: (q) => ((q.state.data as Array<{ status: string }> | undefined)?.some((d) => isActiveCrawlStatus(d.status)) ? 2500 : false),
    },
  });

  const list = domains ?? [];
  const [activeDomainId] = useActiveDomain(domains);
  const activeDomain = list.find((d) => d.id === activeDomainId) ?? null;
  const activeCrawlsList = list.filter((d) => isActiveCrawlStatus(d.status));
  // Kontrol kartları: aktif işler + durdurulmuş/hatalı işler (Devam Et / Yeniden Başlat için)
  const controlList = list.filter((d) => isActiveCrawlStatus(d.status) || d.status === "stopped" || d.status === "error");
  const approvalList = list.filter((d) => d.status === "awaiting_approval");
  const totalDomains = list.length;
  const totalPages = list.reduce((s, d) => s + (d.totalPages ?? 0), 0);
  const scored = list.filter((d) => typeof d.averageScore === "number" && d.averageScore! > 0);
  const avgScore = scored.length ? Math.round(scored.reduce((s, d) => s + (d.averageScore ?? 0), 0) / scored.length) : null;

  const handleDelete = async (id: number) => {
    try {
      await deleteDomain.mutateAsync({ domainId: id });
      toast({ title: "Domain silindi" });
      queryClient.invalidateQueries({ queryKey: getListDomainsQueryKey() });
    } catch {
      toast({ title: "Silme başarısız", variant: "destructive" });
    }
  };

  // ── Tarama kontrolü: Durdur / Devam Et / Sıfırdan Yeniden Başlat ──
  // Durdur: tarama aşamasında nazikçe durur (kısmi veri kaydedilir, durum "Durduruldu");
  //         analiz aşamasında analiz iptal edilir. İş zaten takılıysa direkt işaretler.
  const stopCrawl = async (id: number) => {
    try {
      const r = await fetch(`/api/domains/${id}/stop`, { method: "POST" });
      const data = await r.json().catch(() => ({} as { message?: string; error?: string }));
      if (!r.ok) throw new Error(data.error ?? "");
      toast({ title: "Durduruluyor", description: data.message });
      queryClient.invalidateQueries({ queryKey: getListDomainsQueryKey() });
    } catch (e) {
      toast({ title: "Durdurma başarısız", description: (e as Error).message || undefined, variant: "destructive" });
    }
  };

  // Devam Et: takılı/durdurulmuş işi KALDIĞI AŞAMADAN sürdürür (veriler korunur).
  // İş gerçekten çalışıyorsa backend 409 döner — kullanıcıya aynen iletilir.
  const continueCrawl = async (id: number) => {
    try {
      const r = await fetch(`/api/domains/${id}/restart`, { method: "POST" });
      const data = await r.json().catch(() => ({} as { message?: string; error?: string }));
      if (!r.ok) throw new Error(data.error ?? "");
      toast({ title: "Devam ediliyor", description: data.message });
      queryClient.invalidateQueries({ queryKey: getListDomainsQueryKey() });
    } catch (e) {
      toast({ title: "Devam ettirilemedi", description: (e as Error).message || undefined, variant: "destructive" });
    }
  };

  // Sıfırdan Yeniden Başlat: TÜM tarama verisi silinip baştan taranır (onay ister).
  // Kapsam BÜYÜTÜLEBİLİR (sayfa limiti) ve TEK DİLE daraltılabilir — kullanıcı isteği:
  // "500 dedim ama daha fazlasını taratmak istiyorum, tek dil seçmek istiyorum".
  const [yeniLimit, setYeniLimit] = useState("500");
  const [yeniDil, setYeniDil] = useState("");
  const restartFull = async (id: number) => {
    try {
      const r = await fetch(`/api/domains/${id}/crawl`, {
        method: "POST", headers: { "content-type": "application/json" },
        body: JSON.stringify({ maxPages: parseInt(yeniLimit) || undefined, dilFiltresi: yeniDil || null }),
      });
      if (!r.ok) throw new Error();
      toast({ title: "Sıfırdan tarama başlatıldı", description: `${parseInt(yeniLimit) || "mevcut"} sayfa limiti${yeniDil ? ` · yalnız ${yeniDil.toUpperCase()} sayfalar` : ""}` });
      queryClient.invalidateQueries({ queryKey: getListDomainsQueryKey() });
    } catch {
      toast({ title: "Tarama başlatılamadı", variant: "destructive" });
    }
  };

  const stats = [
    { label: "Domain", value: totalDomains, icon: Globe },
    { label: "Toplam Sayfa", value: totalPages.toLocaleString("tr-TR"), icon: FileText },
    { label: "Ortalama Skor", value: avgScore ?? "—", icon: Gauge },
    { label: "Aktif Tarama", value: activeCrawlsList.length, icon: Activity },
  ];

  return (
    <div className="p-4 md:p-6 max-w-(--breakpoint-2xl) mx-auto space-y-6">
      {/* Karşılama */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          {/* Çekidüzen Adım 0: çok siteli kullanıcı kokpitten filoya tek tıkla dönebilsin */}
          {list.length > 1 && (
            <Link href="/">
              <span className="mb-1 inline-flex cursor-pointer items-center gap-1 text-theme-xs font-medium text-gray-400 hover:text-brand-500">
                ← Tüm siteler
              </span>
            </Link>
          )}
          <h1 className="gl-page-title">Hoş geldin</h1>
          <p className="mt-1 text-theme-sm text-gray-500">
            Genel durumunu özetledim. Yeni bir site analiz etmek için taramayı başlatabilirsin.
          </p>
        </div>
        <Link href="/domains/new">
          <button className="inline-flex items-center gap-2 rounded-lg bg-brand-500 px-4 py-2.5 text-theme-sm font-medium text-white shadow-theme-xs hover:bg-brand-600">
            <Plus className="w-4 h-4" /> Yeni Tarama Başlat
          </button>
        </Link>
      </div>

      {/* TARAMA EN ÜSTTE — süren/durmuş işler her şeyden önce görünür (canlı ilerleme) */}
      {controlList.length > 0 && (
        <div className="space-y-3">
          {controlList.map((d) => {
            // Detay sayfasıyla aynı: tüm pipeline ilerlemesini crawlProgress yansıtır
            // (tarama 0→60, linking 65, clustering 75, analiz 85→99, bitti 100).
            const pct = Math.min(100, Math.max(0, d.crawlProgress ?? 0));
            const active = isActiveCrawlStatus(d.status);
            const cardColor = active ? "#e83e3c" : d.status === "error" ? "#f04438" : "#fb6514";
            return (
              <Card key={d.id} style={{ borderColor: `${cardColor}80`, background: `${cardColor}0f` }}>
                <CardContent className="p-5">
                  <div className="flex items-center justify-between gap-3 mb-3">
                    <div className="flex items-center gap-2 min-w-0">
                      {active
                        ? <Loader2 className="w-4 h-4 animate-spin shrink-0" style={{ color: cardColor }} />
                        : <AlertCircle className="w-4 h-4 shrink-0" style={{ color: cardColor }} />}
                      <span className="font-semibold truncate">{d.url}</span>
                      <span className="text-xs font-medium px-2 py-0.5 rounded-full shrink-0" style={{ background: `${statusColor[d.status]}22`, color: statusColor[d.status] }}>
                        {statusLabels[d.status] ?? d.status}
                      </span>
                    </div>
                    <span className="text-sm font-medium shrink-0">{pct}%</span>
                  </div>
                  <Progress value={pct} className="h-2" />
                  <div className="flex items-center gap-2 mt-3 text-sm text-muted-foreground">
                    <Sparkles className="w-3.5 h-3.5" style={{ color: cardColor }} />
                    {crawlStepText(d)}
                  </div>
                  <div className="mt-3 flex items-center gap-2 flex-wrap">
                    <Link href={`/domains/${d.id}/clusters`}>
                      <Button variant="ghost" size="sm" className="gap-1">Detaya git <ArrowRight className="w-4 h-4" /></Button>
                    </Link>
                    {active && (
                      <Button variant="outline" size="sm" className="border-error-300 text-error-500 gap-1.5"
                        
                        onClick={() => stopCrawl(d.id)}
                        title="Taramayı nazikçe durdurur — o ana kadar taranan sayfalar kaydedilir, sonra Devam Et ile sürdürülebilir.">
                        <Square className="w-3.5 h-3.5" /> Taramayı Durdur
                      </Button>
                    )}
                    <Button variant="outline" size="sm" className="border-brand-300 text-brand-600 dark:text-brand-400 gap-1.5"
                      
                      onClick={() => continueCrawl(d.id)}
                      title="Takılı/durdurulmuş işi kaldığı aşamadan sürdürür (mevcut sayfalar ve cluster yapısı korunur). Gerçekten çalışan bir işi ikilemez.">
                      <Play className="w-3.5 h-3.5" /> Taramaya Devam Et
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="outline" size="sm" className="gap-1.5"
                          title="Tüm tarama verisini silip siteyi baştan tarar.">
                          <RotateCcw className="w-3.5 h-3.5" /> Sıfırdan Yeniden Başlat
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Sıfırdan yeniden taransın mı?</AlertDialogTitle>
                          <AlertDialogDescription>
                            {d.url} için mevcut sayfa, link ve uyarı verileri silinip site baştan taranacak.
                            Kaldığı yerden sürdürmek istiyorsan bunun yerine "Taramaya Devam Et"i kullan.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        {/* Kapsam: limit yükselt + tek dile daralt */}
                        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 p-3 text-sm dark:border-gray-700">
                          <label className="flex items-center gap-2">
                            <span className="text-gray-500 dark:text-gray-400">Sayfa limiti</span>
                            <input type="number" min={10} max={5000} value={yeniLimit} onChange={(e) => setYeniLimit(e.target.value)}
                              className="w-24 rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm outline-none dark:border-gray-700 dark:bg-gray-800 dark:text-white/90" />
                          </label>
                          <label className="flex items-center gap-2">
                            <span className="text-gray-500 dark:text-gray-400">Dil</span>
                            <select value={yeniDil} onChange={(e) => setYeniDil(e.target.value)}
                              className="rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm outline-none dark:border-gray-700 dark:bg-gray-800 dark:text-white/90">
                              <option value="">Tüm diller</option>
                              <option value="tr">Yalnız Türkçe</option>
                              <option value="en">Yalnız İngilizce</option>
                              <option value="de">Yalnız Almanca</option>
                              <option value="fr">Yalnız Fransızca</option>
                              <option value="ar">Yalnız Arapça</option>
                              <option value="ru">Yalnız Rusça</option>
                            </select>
                          </label>
                          <span className="w-full text-[11px] text-gray-400">Tek dil seçersen diğer dillerdeki sayfalar gezilir ama kaydedilmez — limit yalnız seçtiğin dilin sayfalarına işler.</span>
                        </div>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Vazgeç</AlertDialogCancel>
                          <AlertDialogAction onClick={() => restartFull(d.id)}>Evet, sıfırdan tara</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Onay bekleyen: dil seçimi + link önerisi üretimi */}
      {approvalList.length > 0 && (
        <div className="space-y-3">
          {approvalList.map((d) => <ApprovalCard key={d.id} domain={d} />)}
        </div>
      )}

      {/* Özet metrikler — TailAdmin metrik kartları */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4 md:gap-6">
        {stats.map(({ label, value, icon: Icon }) => (
          <div key={label} className="gl-card">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gray-100">
              <Icon className="size-5 text-gray-700" />
            </div>
            <div className="mt-4">
              <span className="gl-label">{label}</span>
              <h4 className="gl-metric">{isLoading ? "…" : value}</h4>
            </div>
          </div>
        ))}
      </div>

      {/* SEÇİLİ SİTE — ajanların öncelikli teknik önerileri (sidebar'daki seçiciyle değişir) */}
      {activeDomain && <AgentRecommendations domainId={activeDomain.id} domainUrl={activeDomain.url} />}

      {/* AKTİF SİTE KARTI — toplu site listesi Portföy'e taşındı; burada yalnız seçili site */}
      <div>
        {isLoading ? (
          <Card className="animate-pulse h-16" />
        ) : list.length > 0 ? (
          <div className="space-y-2">
            {activeDomain && (() => {
              const d = activeDomain;
              return (
                <Card className="hover:border-primary/50 transition-colors">
                  <CardContent className="p-4 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3 min-w-0 cursor-pointer" onClick={() => setLocation(`/domains/${d.id}`)}>
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.06]"><SiteFavicon url={d.url} size={18} /></span>
                      <div className="min-w-0">
                        <div className="font-medium truncate hover:text-primary transition-colors">{d.url}</div>
                        <div className="text-xs text-muted-foreground">
                          Aktif site · son tarama: {d.lastCrawledAt ? new Date(d.lastCrawledAt).toLocaleDateString("tr-TR") : "—"}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-6 shrink-0">
                      <div className="text-right hidden sm:block">
                        <div className="text-xs text-muted-foreground">Sayfa</div>
                        <div className="font-semibold">{(d.totalPages ?? 0).toLocaleString("tr-TR")}</div>
                      </div>
                      <div className="text-right hidden sm:block">
                        <div className="text-xs text-muted-foreground">Skor</div>
                        <div className="font-semibold">{d.averageScore || "—"}</div>
                      </div>
                      <span className="text-xs font-medium px-2.5 py-1 rounded-full" style={{ background: `${statusColor[d.status] ?? "#70706c"}22`, color: statusColor[d.status] ?? "#70706c" }}>
                        {statusLabels[d.status] ?? d.status}
                      </span>
                      {/* YENİDEN TARAMA — tamamlanmış sitede de erişilebilir olmalı (kapsam
                          büyütme + tek dil buradan seçilir); süren taramada gizli. */}
                      {!isActiveCrawlStatus(d.status) && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="outline" size="sm" className="gap-1.5 shrink-0"
                              title="Tüm tarama verisini silip siteyi seçtiğin kapsamla baştan tarar.">
                              <RotateCcw className="w-3.5 h-3.5" /> Yeniden Tara
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Sıfırdan yeniden taransın mı?</AlertDialogTitle>
                              <AlertDialogDescription>
                                {d.url} için mevcut sayfa, link ve uyarı verileri silinip site baştan taranacak.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <div className="flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 p-3 text-sm dark:border-gray-700">
                              <label className="flex items-center gap-2">
                                <span className="text-gray-500 dark:text-gray-400">Sayfa limiti</span>
                                <input type="number" min={10} max={5000} value={yeniLimit} onChange={(e) => setYeniLimit(e.target.value)}
                                  className="w-24 rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm outline-none dark:border-gray-700 dark:bg-gray-800 dark:text-white/90" />
                              </label>
                              <label className="flex items-center gap-2">
                                <span className="text-gray-500 dark:text-gray-400">Dil</span>
                                <select value={yeniDil} onChange={(e) => setYeniDil(e.target.value)}
                                  className="rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm outline-none dark:border-gray-700 dark:bg-gray-800 dark:text-white/90">
                                  <option value="">Tüm diller</option>
                                  <option value="tr">Yalnız Türkçe</option>
                                  <option value="en">Yalnız İngilizce</option>
                                  <option value="de">Yalnız Almanca</option>
                                  <option value="fr">Yalnız Fransızca</option>
                                  <option value="ar">Yalnız Arapça</option>
                                  <option value="ru">Yalnız Rusça</option>
                                </select>
                              </label>
                              <span className="w-full text-[11px] text-gray-400">Tek dil seçersen diğer dillerdeki sayfalar gezilir ama kaydedilmez — limit yalnız seçtiğin dilin sayfalarına işler.</span>
                            </div>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Vazgeç</AlertDialogCancel>
                              <AlertDialogAction onClick={() => restartFull(d.id)}>Evet, sıfırdan tara</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-destructive">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Domaini sil?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Bu işlem geri alınamaz. <strong>{d.url}</strong> ve ilişkili tüm tarama/cluster verileri kalıcı olarak silinecek.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>İptal</AlertDialogCancel>
                            <AlertDialogAction onClick={() => handleDelete(d.id)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Sil</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </CardContent>
                </Card>
              );
            })()}
            {totalDomains > 1 && (
              <div className="text-[12px] text-muted-foreground">
                {totalDomains} siten var — hepsinin sağlık/otorite/AI karşılaştırması{" "}
                <Link href="/"><span className="underline font-medium cursor-pointer text-brand-600 dark:text-brand-400">Tüm Siteler</span></Link> görünümünde.
              </div>
            )}
          </div>
        ) : (
          /* ── KARŞILAMA AKIŞI: yeni kullanıcı boş ekranla baş başa kalmasın ── */
          <Card>
            <CardContent className="p-6 md:p-8">
              <h3 className="text-xl font-bold text-gray-800 dark:text-white/90">Geolop'a hoş geldin 👋</h3>
              <p className="mt-1 mb-6 text-theme-sm text-gray-500 dark:text-gray-400">
                Dört adımda ilk analizini al — toplam 10 dakikanı alır, gerisini ajanlar sürdürür.
              </p>
              <div className="grid gap-3 md:grid-cols-2">
                {[
                  { n: 1, t: "Siteni ekle", d: "Adresini yaz, tarama sınırını seç — sayfaların ve iç link yapın çıkarılır.", href: "/domains/new", cta: "Yeni Tarama Başlat", primary: true },
                  { n: 2, t: "Google'ı bağla", d: "Arama ve trafik verilerin (tıklama, gösterim, kullanıcı) analizlere katılır.", href: "/settings", cta: "Ayarlar" },
                  { n: 3, t: "Takip listeni kur", d: "Önemli kelimelerini ve yapay zekâya sorulan soruları ekle — her gece otomatik ölçülür.", href: "/rank-tracking", cta: "Sıralama Takibi" },
                  { n: 4, t: "Planını al", d: "Tüm ajan verileri okunur, 'önce ne yapmalıyım' listesi ve takvim çıkar.", href: "/planning", cta: "Planlama" },
                ].map((s) => (
                  <div key={s.n} className="flex items-start gap-3 rounded-2xl border border-gray-200 p-4 dark:border-gray-800">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-50 text-sm font-bold text-brand-600 dark:bg-brand-500/15 dark:text-brand-400">{s.n}</span>
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-800 dark:text-white/90">{s.t}</p>
                      <p className="mt-0.5 text-theme-xs text-gray-500 dark:text-gray-400">{s.d}</p>
                      <Link href={s.href}>
                        <Button size="sm" variant={s.primary ? "default" : "outline"} className="mt-2.5">{s.cta}</Button>
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
              <p className="mt-5 text-theme-xs text-gray-400">
                İpucu: sol alttaki Geolop Asistanı'na sesli ya da yazılı "siteme genel bakış ver" diyebilirsin — adımları senin yerine o da başlatabilir.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
